/**
 * Staff Console -> Links & Payout tab.
 *
 * Tracked short links that Eli and TJ paste into LinkedIn DMs
 * (https://dealcloserautopilot.com/r/<code>). Each visit is logged and
 * redirected with utm_campaign=<code>, so a signup that lands with that
 * cookie attributes back to the link via user_attribution. The payout card
 * applies the agreed formula over the selected date range:
 *
 *   flat retainer + $25 per link-attributed signup + $10 per booked call
 *   (booked calls are counted platform-wide, not only referred users).
 */

import { useMemo, useState } from 'react'
import {
  Loader2,
  RefreshCw,
  Link2,
  MousePointerClick,
  UserPlus,
  CreditCard,
  PhoneCall,
  DollarSign,
  Copy,
  Check,
  Plus,
  Archive,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtLocale } from '@/lib/dates'
import {
  useAdminLinks,
  useAdminUsers,
  useCreateAdminLink,
  useArchiveAdminLink,
  type AdminLinkRow,
  type AdminLinksRange,
} from './use-admin'

const NO_OWNER = '__none__'

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function daysAgo(n: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - n)
  return isoDay(d)
}

function monthStart(): string {
  const d = new Date()
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`
}

function usd(n: number): string {
  return '$' + Math.round(n).toLocaleString()
}

function pct(part: number, whole: number): string {
  if (whole <= 0) return '0%'
  return `${Math.round((part / whole) * 100)}%`
}

const PRESETS: { key: string; label: string; range: () => AdminLinksRange }[] = [
  { key: '7d', label: '7d', range: () => ({ from: daysAgo(6), to: isoDay(new Date()) }) },
  { key: '30d', label: '30d', range: () => ({ from: daysAgo(29), to: isoDay(new Date()) }) },
  { key: 'month', label: 'This month', range: () => ({ from: monthStart(), to: isoDay(new Date()) }) },
]

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
  hint,
}: {
  label: string
  value: string
  icon: LucideIcon
  tone: string
  hint?: string
}) {
  return (
    <Card className="flex-1 min-w-[150px]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="eyebrow text-[var(--color-silver)]">{label}</p>
          <Icon className={cn('h-4 w-4', tone)} />
        </div>
        <div className={cn('mt-1 text-2xl font-bold tracking-tight tabular-nums', tone)}>{value}</div>
        {hint && <p className="mt-1 text-xs text-[var(--color-silver)]">{hint}</p>}
      </CardContent>
    </Card>
  )
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      title="Copy share link"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true)
          window.setTimeout(() => setCopied(false), 1500)
        })
      }}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.05)] hover:text-[var(--color-silver-bright)]"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-[#34D399]" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

function NewLinkForm({ onCreated }: { onCreated: () => void }) {
  const { data: users } = useAdminUsers()
  const create = useCreateAdminLink()
  const [code, setCode] = useState('')
  const [label, setLabel] = useState('')
  const [destination, setDestination] = useState('')
  const [owner, setOwner] = useState<string>(NO_OWNER)
  const [error, setError] = useState<string | null>(null)

  const normalisedCode = code.trim().toLowerCase()
  const codeOk = /^[a-z0-9-]{2,32}$/.test(normalisedCode)
  const canSubmit = codeOk && label.trim().length > 0 && !create.isPending

  const submit = () => {
    setError(null)
    create.mutate(
      {
        code: normalisedCode,
        label: label.trim(),
        destination: destination.trim() || undefined,
        owner_user_id: owner === NO_OWNER ? null : owner,
      },
      {
        onSuccess: () => {
          setCode('')
          setLabel('')
          setDestination('')
          setOwner(NO_OWNER)
          onCreated()
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create link'),
      },
    )
  }

  const sortedUsers = useMemo(
    () => (users ?? []).slice().sort((a, b) => (a.email ?? '').localeCompare(b.email ?? '')),
    [users],
  )

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
          <Plus className="h-4 w-4 text-[#34D399]" />
          New link
          <span className="text-xs font-normal text-[var(--color-silver)]">
            (share as https://dealcloserautopilot.com/r/code)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="link-code" className="text-xs text-[var(--color-silver)]">
              Code
            </Label>
            <Input
              id="link-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="eli"
              maxLength={32}
              className={cn(code.length > 0 && !codeOk && 'border-[#F87171]')}
            />
            <p className="text-[11px] text-[var(--color-silver)]">
              2 to 32 chars: a-z, 0-9, hyphen. Becomes utm_campaign.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="link-label" className="text-xs text-[var(--color-silver)]">
              Label
            </Label>
            <Input
              id="link-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Eli LinkedIn DMs"
              maxLength={120}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="link-destination" className="text-xs text-[var(--color-silver)]">
              Destination (optional)
            </Label>
            <Input
              id="link-destination"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="https://dealcloserautopilot.com/"
            />
            <p className="text-[11px] text-[var(--color-silver)]">https only. Defaults to the homepage.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[var(--color-silver)]">Owner (optional)</Label>
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger>
                <SelectValue placeholder="No owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_OWNER}>No owner</SelectItem>
                {sortedUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Button size="sm" onClick={submit} disabled={!canSubmit}>
            {create.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
            Create link
          </Button>
          {error && <span className="text-xs text-[#F87171]">{error}</span>}
        </div>
      </CardContent>
    </Card>
  )
}

function LinkRow({ link }: { link: AdminLinkRow }) {
  const archive = useArchiveAdminLink()
  const [confirming, setConfirming] = useState(false)
  return (
    <TableRow>
      <TableCell>
        <div className="text-sm font-medium text-[var(--color-silver-bright)]">{link.label}</div>
        <div className="text-xs text-[var(--color-silver)]">
          {link.owner_email ?? 'no owner'}
          <span className="mx-1">/</span>
          created {fmtLocale(link.created_at, 'date')}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="border-none bg-[rgba(255,255,255,0.06)] font-mono text-xs text-[var(--color-silver-bright)]">
          {link.code}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <span className="max-w-[260px] truncate font-mono text-xs text-[var(--color-silver)]" title={link.destination}>
            {link.share_url}
          </span>
          <CopyButton text={link.share_url} />
        </div>
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">{link.clicks.toLocaleString()}</TableCell>
      <TableCell className="text-right text-sm tabular-nums">{link.unique_visitors.toLocaleString()}</TableCell>
      <TableCell className="text-right text-sm font-semibold tabular-nums text-[#8FB4FA]">
        {link.signups.toLocaleString()}
        <span className="ml-1 text-xs font-normal text-[var(--color-silver)]">
          ({pct(link.signups, link.unique_visitors)})
        </span>
      </TableCell>
      <TableCell className="text-right text-sm font-semibold tabular-nums text-[#34D399]">
        {link.paid.toLocaleString()}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums text-[#FBBF24]">
        {link.booked_calls.toLocaleString()}
      </TableCell>
      <TableCell className="text-right">
        {confirming ? (
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="destructive"
              className="h-7 px-2 text-xs"
              disabled={archive.isPending}
              onClick={() => archive.mutate(link.code, { onSettled: () => setConfirming(false) })}
            >
              {archive.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Archive'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <button
            type="button"
            title="Archive link (stops redirecting; history is kept)"
            onClick={() => setConfirming(true)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.05)] hover:text-[#F87171]"
          >
            <Archive className="h-3.5 w-3.5" />
          </button>
        )}
      </TableCell>
    </TableRow>
  )
}

export function LinksPage() {
  const [range, setRange] = useState<AdminLinksRange>(() => PRESETS[1]!.range())
  const [draft, setDraft] = useState<AdminLinksRange>(range)
  const { data, isLoading, isError, error, refetch, isRefetching } = useAdminLinks(range)

  const applyDraft = () => {
    if (!draft.from || !draft.to) return
    setRange(draft.from <= draft.to ? draft : { from: draft.to, to: draft.from })
  }

  const activePreset = PRESETS.find((p) => {
    const r = p.range()
    return r.from === range.from && r.to === range.to
  })?.key

  const totals = useMemo(() => {
    const links = data?.links ?? []
    return {
      clicks: links.reduce((a, l) => a + l.clicks, 0),
      unique: links.reduce((a, l) => a + l.unique_visitors, 0),
      signups: links.reduce((a, l) => a + l.signups, 0),
      paid: links.reduce((a, l) => a + l.paid, 0),
      booked: links.reduce((a, l) => a + l.booked_calls, 0),
    }
  }, [data])

  return (
    <div className="space-y-4">
      {/* Date range */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="flex items-center gap-1">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                size="sm"
                variant={activePreset === p.key ? 'default' : 'outline'}
                className="h-8 px-3 text-xs"
                onClick={() => {
                  const r = p.range()
                  setDraft(r)
                  setRange(r)
                }}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="space-y-1">
            <Label htmlFor="links-from" className="text-xs text-[var(--color-silver)]">
              From
            </Label>
            <Input
              id="links-from"
              type="date"
              value={draft.from}
              max={draft.to}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              className="h-8 w-[150px] text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="links-to" className="text-xs text-[var(--color-silver)]">
              To
            </Label>
            <Input
              id="links-to"
              type="date"
              value={draft.to}
              min={draft.from}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              className="h-8 w-[150px] text-xs"
            />
          </div>
          <Button size="sm" variant="outline" className="h-8 px-3 text-xs" onClick={applyDraft}>
            Apply
          </Button>
          <div className="ml-auto flex items-center gap-2 text-xs text-[var(--color-silver)]">
            <span>
              {range.from} to {range.to} (UTC, inclusive)
            </span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="flex items-center gap-1 rounded-md px-2 py-1 hover:bg-[rgba(255,255,255,0.05)]"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
              Refresh
            </button>
          </div>
        </CardContent>
      </Card>

      {isLoading || !data ? (
        isError ? (
          <div className="p-6 text-sm text-[#F87171]">
            Failed to load links: {error instanceof Error ? error.message : 'unknown error'}
          </div>
        ) : (
          <div className="flex items-center justify-center p-12 text-[var(--color-silver)]">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading links...
          </div>
        )
      ) : (
        <>
          {/* Link totals */}
          <div className="flex flex-wrap gap-3">
            <StatCard label="Clicks" value={totals.clicks.toLocaleString()} icon={MousePointerClick} tone="text-[var(--color-silver-bright)]" hint={`${totals.unique.toLocaleString()} unique visitors`} />
            <StatCard label="Link signups" value={totals.signups.toLocaleString()} icon={UserPlus} tone="text-[#8FB4FA]" hint="attributed via utm_campaign" />
            <StatCard label="Link paid" value={totals.paid.toLocaleString()} icon={CreditCard} tone="text-[#34D399]" hint={`${pct(totals.paid, totals.signups)} of link signups`} />
            <StatCard label="Referred users' calls" value={totals.booked.toLocaleString()} icon={PhoneCall} tone="text-[#FBBF24]" hint="booked by users who came via a link" />
          </div>

          {/* Platform + payout */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
                  <PhoneCall className="h-4 w-4 text-[#FBBF24]" />
                  Platform totals
                  <span className="text-xs font-normal text-[var(--color-silver)]">(all users, selected range)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-3">
                  <div className="flex-1 min-w-[110px] rounded-md bg-[rgba(255,255,255,0.04)] p-3">
                    <p className="eyebrow text-[var(--color-silver)]">Signups</p>
                    <p className="text-xl font-bold tabular-nums text-[#8FB4FA]">{data.platform.signups.toLocaleString()}</p>
                  </div>
                  <div className="flex-1 min-w-[110px] rounded-md bg-[rgba(255,255,255,0.04)] p-3">
                    <p className="eyebrow text-[var(--color-silver)]">Paid</p>
                    <p className="text-xl font-bold tabular-nums text-[#34D399]">{data.platform.paid.toLocaleString()}</p>
                  </div>
                  <div className="flex-1 min-w-[110px] rounded-md bg-[rgba(255,255,255,0.04)] p-3">
                    <p className="eyebrow text-[var(--color-silver)]">Booked calls</p>
                    <p className="text-xl font-bold tabular-nums text-[#FBBF24]">{data.platform.booked_calls.toLocaleString()}</p>
                  </div>
                </div>
                {data.platform.booked_calls_by_user.length > 0 ? (
                  <div>
                    <p className="mb-1 text-xs text-[var(--color-silver)]">Booked calls by user</p>
                    <div className="flex flex-wrap gap-2">
                      {data.platform.booked_calls_by_user.map((u) => (
                        <Badge
                          key={u.user_id}
                          variant="outline"
                          className="border-none bg-[rgba(255,255,255,0.06)] px-3 py-1.5 text-xs text-[var(--color-silver-bright)]"
                          title={u.user_id}
                        >
                          {u.email ?? u.user_id}
                          <span className="ml-2 font-bold tabular-nums text-[#FBBF24]">{u.count}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-[var(--color-silver)]">
                    No booked calls in this range. A call counts when a lead reaches meeting booked or won.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-[rgba(16,185,129,0.25)]">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
                  <DollarSign className="h-4 w-4 text-[#34D399]" />
                  Payout
                  <span className="text-xs font-normal text-[var(--color-silver)]">({data.range.from} to {data.range.to})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--color-silver)]">Flat</span>
                    <span className="tabular-nums text-[var(--color-silver-bright)]">{usd(data.payout.flat_usd)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--color-silver)]">
                      Link signups: {data.payout.link_signups.toLocaleString()} x {usd(data.payout.per_signup_usd)}
                    </span>
                    <span className="tabular-nums text-[var(--color-silver-bright)]">
                      {usd(data.payout.link_signups * data.payout.per_signup_usd)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--color-silver)]">
                      Booked calls (platform): {data.payout.platform_booked_calls.toLocaleString()} x {usd(data.payout.per_booked_call_usd)}
                    </span>
                    <span className="tabular-nums text-[var(--color-silver-bright)]">
                      {usd(data.payout.platform_booked_calls * data.payout.per_booked_call_usd)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                    <span className="font-semibold text-[var(--color-silver-bright)]">Total</span>
                    <span className="text-2xl font-bold tabular-nums text-[#34D399]">{usd(data.payout.total_usd)}</span>
                  </div>
                  <p className="text-xs text-[var(--color-silver)]">
                    Formula: {usd(data.payout.flat_usd)} flat + {usd(data.payout.per_signup_usd)} per signup attributed to a link + {usd(data.payout.per_booked_call_usd)} per booked call platform-wide.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Per-link table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
                <Link2 className="h-4 w-4 text-[#8FB4FA]" />
                Tracked links
                <span className="text-xs font-normal text-[var(--color-silver)]">
                  (clicks, signups and calls within the selected range)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.links.length === 0 ? (
                <p className="py-4 text-sm text-[var(--color-silver)]">
                  No links yet. Create one below, then share the /r/ URL in DMs.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Share URL</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead className="text-right">Unique</TableHead>
                      <TableHead className="text-right">Signups</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Booked calls</TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.links.map((l) => (
                      <LinkRow key={l.code} link={l} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <NewLinkForm onCreated={() => void refetch()} />
        </>
      )}
    </div>
  )
}
